import { useEffect, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from "antd"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { gen, getActingUserRequestFields, swimminglyApi } from "./utils"
import UpdateMeetParticipatingClubsVirtual from "./UpdateMeetParticipatingClubsVirtual"
import UpdateVirtualMeetScoringModal from "./UpdateVirtualMeetScoringModal"

const { Text } = Typography

interface IVirtualMeetShellDetails {
  virtualMeetId: number
  organizerClubId: number
  meetName: string
  meetdate: string
  startdate: string | null
  meetTime: string | null
  course: number
  individualLimit: number | null
  relayLimit: number | null
  totalLimit: number | null
  pendingInvitations: number
  acceptedInvitations: number
  declinedInvitations: number
  removedInvitations: number
}

interface IVirtualMeetShellEditorModalProps {
  visible: boolean
  setVisible: (newVisible: boolean) => void
  virtualMeetId: number | null
  refresh: () => void
}

const formatCourseLabel = (course: number | null | undefined): string => {
  if (course === 1) return "SCM"
  if (course === 2) return "LCM"
  return "SCY"
}

const normalizeLimitValue = (value: number | string | null | undefined): number | null => {
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : null
  }

  if (value === null || value === undefined || value === "") {
    return null
  }

  const parsedValue = parseInt(String(value), 10)
  return Number.isInteger(parsedValue) ? parsedValue : null
}

export default function VirtualMeetShellEditorModal({
  visible,
  setVisible,
  virtualMeetId,
  refresh,
}: IVirtualMeetShellEditorModalProps): JSX.Element {
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )
  const actingUser = aliasedUser || user
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [shell, setShell] = useState<IVirtualMeetShellDetails | null>(null)
  const [meetName, setMeetName] = useState("")
  const [startDate, setStartDate] = useState<Dayjs | null>(null)
  const [endDate, setEndDate] = useState<Dayjs | null>(null)
  const [meetTime, setMeetTime] = useState("")
  const [course, setCourse] = useState<number>(0)
  const [hasIndividualLimit, setHasIndividualLimit] = useState(false)
  const [hasRelayLimit, setHasRelayLimit] = useState(false)
  const [hasTotalLimit, setHasTotalLimit] = useState(false)
  const [individualLimit, setIndividualLimit] = useState<number | null>(null)
  const [relayLimit, setRelayLimit] = useState<number | null>(null)
  const [totalLimit, setTotalLimit] = useState<number | null>(null)
  const [participatingClubsVisible, setParticipatingClubsVisible] = useState(false)
  const [scoringVisible, setScoringVisible] = useState(false)

  useEffect(() => {
    if (!visible || !virtualMeetId) {
      return
    }

    let mounted = true
    setLoading(true)

    swimminglyApi
      .get(gen(`/api/getVirtualMeetShellDetails/${virtualMeetId}`))
      .then((data: { status: string; shell?: IVirtualMeetShellDetails; message?: string }) => {
        if (!mounted) {
          return
        }

        if (data.status !== "success" || !data.shell) {
          message.error(data.message || "Problem loading virtual meet details.")
          setShell(null)
          return
        }

        setShell(data.shell)
        setMeetName(data.shell.meetName || "")
        setStartDate(data.shell.startdate ? dayjs(data.shell.startdate) : null)
        setEndDate(data.shell.meetdate ? dayjs(data.shell.meetdate) : null)
        setMeetTime(data.shell.meetTime || "")
        setCourse(data.shell.course ?? 0)
        const nextIndividualLimit = normalizeLimitValue(data.shell.individualLimit)
        const nextRelayLimit = normalizeLimitValue(data.shell.relayLimit)
        const nextTotalLimit = normalizeLimitValue(data.shell.totalLimit)
        setHasIndividualLimit(nextIndividualLimit !== null)
        setHasRelayLimit(nextRelayLimit !== null)
        setHasTotalLimit(nextTotalLimit !== null)
        setIndividualLimit(nextIndividualLimit)
        setRelayLimit(nextRelayLimit)
        setTotalLimit(nextTotalLimit)
      })
      .catch(() => {
        if (mounted) {
          message.error("Problem loading virtual meet details.")
          setShell(null)
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false)
        }
      })

    return () => {
      mounted = false
    }
  }, [visible, virtualMeetId])

  const closeModal = () => {
    if (saving) {
      return
    }

    setVisible(false)
  }

  const reloadShell = () => {
    refresh()
    if (!virtualMeetId) {
      return
    }

    swimminglyApi
      .get(gen(`/api/getVirtualMeetShellDetails/${virtualMeetId}`))
      .then((data: { status: string; shell?: IVirtualMeetShellDetails }) => {
        if (data.status === "success" && data.shell) {
          setShell(data.shell)
          setMeetName(data.shell.meetName || "")
          setStartDate(data.shell.startdate ? dayjs(data.shell.startdate) : null)
          setEndDate(data.shell.meetdate ? dayjs(data.shell.meetdate) : null)
          setMeetTime(data.shell.meetTime || "")
          setCourse(data.shell.course ?? 0)
          const nextIndividualLimit = normalizeLimitValue(data.shell.individualLimit)
          const nextRelayLimit = normalizeLimitValue(data.shell.relayLimit)
          const nextTotalLimit = normalizeLimitValue(data.shell.totalLimit)
          setHasIndividualLimit(nextIndividualLimit !== null)
          setHasRelayLimit(nextRelayLimit !== null)
          setHasTotalLimit(nextTotalLimit !== null)
          setIndividualLimit(nextIndividualLimit)
          setRelayLimit(nextRelayLimit)
          setTotalLimit(nextTotalLimit)
        }
      })
      .catch(() => null)
  }

  const saveEntryLimits = async (confirmOverLimit = false): Promise<boolean> => {
    if (!virtualMeetId || !shell) {
      return true
    }

    if (hasIndividualLimit && individualLimit === null) {
      message.error("Please enter an individual entry limit or leave it as No Limit.")
      return false
    }

    if (hasRelayLimit && relayLimit === null) {
      message.error("Please enter a relay entry limit or leave it as No Limit.")
      return false
    }

    if (hasTotalLimit && totalLimit === null) {
      message.error("Please enter a total entry limit or leave it as No Limit.")
      return false
    }

    const nextIndividualLimit = hasIndividualLimit ? individualLimit : null
    const nextRelayLimit = hasRelayLimit ? relayLimit : null
    const nextTotalLimit = hasTotalLimit ? totalLimit : null

    if (
      nextIndividualLimit === normalizeLimitValue(shell.individualLimit) &&
      nextRelayLimit === normalizeLimitValue(shell.relayLimit) &&
      nextTotalLimit === normalizeLimitValue(shell.totalLimit)
    ) {
      return true
    }

    try {
      const response = await swimminglyApi
        .post(gen("/api/updateMeetEntryLimits"))
        .body({
          virtualMeetId,
          individualEntryLimit: nextIndividualLimit,
          relayEntryLimit: nextRelayLimit,
          totalEntryLimit: nextTotalLimit,
          confirmOverLimit,
          ...getActingUserRequestFields(actingUser),
        })

      if (
        response?.status === "warning" &&
        response?.code === "entry-limit-overage-warning" &&
        !confirmOverLimit
      ) {
        return await new Promise((resolve) => {
          Modal.confirm({
            title: "Save shared virtual meet entry limits?",
            content:
              response.message ||
              "Some swimmers already exceed the new shared virtual meet entry limits. Existing entries will remain, but future entry edits will enforce the new limits.",
            okText: "Save limits",
            cancelText: "Cancel",
            onOk: async () => resolve(await saveEntryLimits(true)),
            onCancel: () => resolve(false),
          })
        })
      }

      if (response?.status !== "success") {
        throw new Error(response?.message || "Problem updating virtual meet entry limits.")
      }

      return true
    } catch (error) {
      console.error(error)
      message.error("Problem updating virtual meet entry limits.")
      return false
    }
  }

  const onSave = async () => {
    if (!shell || !virtualMeetId) {
      return
    }

    const trimmedMeetName = meetName.trim()

    if (!trimmedMeetName) {
      message.error("Meet name is required.")
      return
    }

    if (!startDate || !endDate || !meetTime.trim()) {
      message.error("Meet window start, end, and time are required.")
      return
    }

    const hasNameChange = trimmedMeetName !== shell.meetName
    const hasDateTimeChange =
      startDate.format("YYYY-MM-DD") !== (shell.startdate || "") ||
      endDate.format("YYYY-MM-DD") !== shell.meetdate ||
      meetTime.trim() !== (shell.meetTime || "")
    const hasCourseChange = course !== shell.course
    const hasEntryLimitChange =
      (hasIndividualLimit ? individualLimit : null) !== normalizeLimitValue(shell.individualLimit) ||
      (hasRelayLimit ? relayLimit : null) !== normalizeLimitValue(shell.relayLimit) ||
      (hasTotalLimit ? totalLimit : null) !== normalizeLimitValue(shell.totalLimit)

    if (!hasNameChange && !hasDateTimeChange && !hasCourseChange && !hasEntryLimitChange) {
      closeModal()
      return
    }

    setSaving(true)

    try {
      if (hasNameChange) {
        const response = await swimminglyApi
          .post(gen("/api/updateMeetName"))
          .body({ virtualMeetId, meetName: trimmedMeetName })

        if (response?.status !== "success") {
          message.error(response?.message || response?.error || "Problem updating virtual meet.")
          return
        }
      }

      if (hasDateTimeChange) {
        const response = await swimminglyApi
          .post(gen("/api/updateMeetDateTime"))
          .body({
            virtualMeetId,
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: endDate.format("YYYY-MM-DD"),
            meetTime: meetTime.trim(),
            ...getActingUserRequestFields(actingUser),
          })

        if (response?.status !== "success") {
          message.error(response?.message || response?.error || "Problem updating virtual meet.")
          return
        }
      }

      if (hasCourseChange) {
        const response = await swimminglyApi
          .post(gen("/api/updateVirtualMeetCourse"))
          .body({ virtualMeetId, newCourse: course })

        if (response?.status !== "success") {
          message.error(response?.message || response?.error || "Problem updating virtual meet.")
          return
        }
      }

      if (hasEntryLimitChange) {
        const savedLimits = await saveEntryLimits()

        if (!savedLimits) {
          return
        }
      }

      message.success("Virtual meet updated successfully.")
      reloadShell()
      setVisible(false)
    } catch (_error) {
      message.error("Problem updating virtual meet.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        title="Manage Virtual Meet"
        open={visible}
        onCancel={closeModal}
        onOk={onSave}
        confirmLoading={saving}
        width={720}
        okText="Save Shared Details"
      >
        {loading || !shell ? (
          <Text type="secondary">Loading virtual meet...</Text>
        ) : (
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            <Space size={8} wrap>
              <Tag color="gold">Pending {shell.pendingInvitations}</Tag>
              <Tag color="green">Accepted {shell.acceptedInvitations}</Tag>
              <Tag>Declined {shell.declinedInvitations}</Tag>
              <Tag color="orange">Removed {shell.removedInvitations}</Tag>
            </Space>
            <Form layout="vertical">
              <Form.Item label="Meet Name" required>
                <Input value={meetName} onChange={(event) => setMeetName(event.target.value)} />
              </Form.Item>
              <Form.Item label="Meet Window" required>
                <Space style={{ width: "100%" }} wrap>
                  <DatePicker
                    value={startDate}
                    onChange={(value) => setStartDate(value)}
                    placeholder="Start date"
                  />
                  <DatePicker
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                    placeholder="End date"
                  />
                  <Input
                    value={meetTime}
                    onChange={(event) => setMeetTime(event.target.value)}
                    placeholder="Meet time (for example 6:00 PM)"
                    style={{ minWidth: 220 }}
                  />
                </Space>
              </Form.Item>
              <Form.Item label="Virtual Course">
                <Select value={course} onChange={setCourse} style={{ width: 160 }}>
                  <Select.Option value={0}>SCY</Select.Option>
                  <Select.Option value={1}>SCM</Select.Option>
                  <Select.Option value={2}>LCM</Select.Option>
                </Select>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Current virtual meet course: {formatCourseLabel(shell.course)}</Text>
                </div>
              </Form.Item>
              <Form.Item label="Shared Entry Limits">
                <Space orientation="vertical" size={10} style={{ width: "100%" }}>
                  {[
                    {
                      key: "individual",
                      label: "Individual",
                      checked: hasIndividualLimit,
                      value: individualLimit,
                      setChecked: setHasIndividualLimit,
                      setValue: setIndividualLimit,
                    },
                    {
                      key: "relay",
                      label: "Relay",
                      checked: hasRelayLimit,
                      value: relayLimit,
                      setChecked: setHasRelayLimit,
                      setValue: setRelayLimit,
                    },
                    {
                      key: "total",
                      label: "Total",
                      checked: hasTotalLimit,
                      value: totalLimit,
                      setChecked: setHasTotalLimit,
                      setValue: setTotalLimit,
                    },
                  ].map((limit) => (
                    <Space key={limit.key} align="center" wrap>
                      <Checkbox
                        checked={limit.checked}
                        onChange={(event) => {
                          limit.setChecked(event.target.checked)
                          if (!event.target.checked) {
                            limit.setValue(null)
                          }
                        }}
                      >
                        {limit.label}
                      </Checkbox>
                      <InputNumber
                        min={0}
                        disabled={!limit.checked}
                        value={limit.value ?? undefined}
                        onChange={(value) => {
                          limit.setValue(typeof value === "number" ? value : null)
                        }}
                        placeholder="No Limit"
                      />
                    </Space>
                  ))}
                  <Text type="secondary">
                    Updating these limits changes the shared virtual meet and all accepted constituent meets.
                  </Text>
                </Space>
              </Form.Item>
            </Form>
            <Space wrap>
              <Button onClick={() => setParticipatingClubsVisible(true)}>
                Manage Participating Clubs
              </Button>
              <Button onClick={() => setScoringVisible(true)}>
                Update Scoring
              </Button>
            </Space>
            <Text type="secondary">
              Organizer edits here update the virtual meet directly. Accepted clubs keep their own pool and roster selections.
            </Text>
          </Space>
        )}
      </Modal>
      <UpdateMeetParticipatingClubsVirtual
        visible={participatingClubsVisible}
        setVisible={setParticipatingClubsVisible}
        meetId={null}
        virtualMeetId={virtualMeetId || 0}
        homeClubId={shell?.organizerClubId || null}
        meetClubSeasons={[]}
        refresh={reloadShell}
      />
      {virtualMeetId ? (
        <UpdateVirtualMeetScoringModal
          visible={scoringVisible}
          meet={{ virtualMeetId }}
          unMountParent={() => setScoringVisible(false)}
          refreshPage={reloadShell}
        />
      ) : null}
    </>
  )
}
